import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { portalConst } from '@app/config';
import { ActivityService, AlertService, NotificationService } from '@app/core';
import { Attachment, TypeAttachment } from '@app/core/models';
import { AnswerService } from '@app/core/services/answers.service';
import { AttachmentService } from '@app/core/services/attachment.service';
import {
  AttachmentDropzoneComponent,
  ExistingAttachment,
} from '@app/modules/eclass/components/attachment-dropzone/attachment-dropzone.component';
import { ModalFileViewerComponent } from '@app/modules/eclass/components/modal-file-viewer/modal-file-viewer.component';
import { SHARED_MODULES, SidenavDirection, SidenavGroupComponent } from '@app/shared';
import { TextEditorComponent } from '@app/shared/modules/text-editor/text-editor.component';
import { downloadFile, safeEmpty } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { differenceInMilliseconds, isBefore, milliseconds, parse } from 'date-fns';
import { flattenDeep, isEmpty } from 'lodash';
import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import { BehaviorSubject, filter, switchMap, tap } from 'rxjs';
import { answerStatus } from '../../utils/activity';
import { SidenavAnswerComponent } from './sidenav-answer.component';
import { UploadFile } from '@app/modules/eclass/components/attachment-dropzone/dropzone.types';

@Component({
  imports: [SHARED_MODULES, TextEditorComponent, AttachmentDropzoneComponent, SidenavAnswerComponent],
  templateUrl: './modal-answer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalAnswerComponent implements OnInit {
  public readonly dialogRef = inject<DialogRef<boolean>>(DialogRef);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _cdRef = inject(ChangeDetectorRef);
  private readonly _dialog = inject(Dialog);
  private readonly _alertService = inject(AlertService);
  private readonly _answerService = inject(AnswerService);
  private readonly _activityService = inject(ActivityService);
  private readonly _translateService = inject(TranslateService);
  private readonly _attachmentService = inject(AttachmentService);
  private readonly _notificationService = inject(NotificationService);
  private readonly store = inject<Store<AppState>>(Store);
  public readonly data = inject(DIALOG_DATA);
  @ViewChild(SidenavGroupComponent, { static: true }) private _sidenav: SidenavGroupComponent;
  public readonly ckeditorUploadImageUrl = `${portalConst.api.usuario}/v2/upload/ckeditor-image?module=eclass`;
  public readonly direction = SidenavDirection;

  public submitted$ = new BehaviorSubject<boolean>(false);
  public isExternal$ = this.store.select(AppSelectors.isExternal);
  public disable: boolean;
  public hasFeedback = false;
  public situationalPhrase = '';
  public form: FormGroup;
  public feedbacks: any[];
  public controls: any;
  public answer: any;
  public info: any;
  public files = [];
  public status: number = 1;

  public config: DropzoneConfigInterface = {
    clickable: true,
    url: `${portalConst.api.eclass}/v1/upload/attachment`,
    createImageThumbnails: false,
    maxFiles: 5,
    maxFilesize: 200,
    acceptedFiles: '.pdf,.mp3,.mp4,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpeg,.jpg,.png,.txt',
    previewsContainer: false,
    params: { folder: 'answers' },
  };

  public ngOnInit() {
    this.initForm();
    this.getActivityAnswer();
  }

  public get existingAttachments(): ExistingAttachment[] {
    return this.files
      .filter((file) => file.id !== null)
      .map((file) => ({
        id: file.id,
        name: file.title ?? file.file,
        size: file.size ?? 0,
        url: file.url,
        mimeType: file.type,
      }));
  }

  public openFile(file): void {
    if (file.type === 1) {
      if (file.vimeo_hash) {
        this.openVideo(`${portalConst.vimeo_url}${file.file}?h=${file.vimeo_hash}`);
      } else {
        this._attachmentService
          .checkVideo({ id: file.id, video: `/videos/${file.file}`, conta: file.vimeo_account })
          .subscribe((resp) => this.openVideo(`${portalConst.vimeo_url}${file.file}?h=${resp.vimeo_hash}`));
      }
    } else {
      this.openVideo(file.url);
    }
  }

  public getActivityAnswer() {
    this._activityService
      .show(this.data?.activityId)
      .pipe(
        safeEmpty(),
        tap((activity) => {
          this.status = answerStatus(activity)?.status;
        }),
        tap((res) => {
          this.form.get('activity_id').setValue(res.id);

          this.controls = {
            ...this.controls,
            deadline: Math.floor(
              differenceInMilliseconds(parse(res.date_end, 'yyyy-MM-dd HH:mm:ss', new Date()), new Date()) /
                milliseconds({ days: 1 }),
            ),
            beforeDeadline: isBefore(new Date(), parse(res.date_end, 'yyyy-MM-dd HH:mm:ss', new Date())),
          };

          this.info = {
            activity_id: res.id,
            classDate: res.created_at,
            subject: res.daily.subject.descricao,
            deadLine: res.date_end,
            title: res.title,
            content: res.description,
            attachments: res.attachments,
          };
        }),
        switchMap((res) => this._activityService.answer(res.id).pipe(safeEmpty())),
      )
      .subscribe((res) => {
        if (!!res?.content?.id) {
          this.form.get('id').setValue(res?.content.id);
        }

        if (!isEmpty(res.content?.feedback)) {
          this.hasFeedback = true;
        }

        if (!!res.content?.score || !isEmpty(res.content?.feedback)) {
          this._sidenav?.sideEnd();
        }

        this.form.get('answer').setValue(res.content?.answer);

        this.controls = {
          ...this.controls,
          allow: res.allow,
          score: res.content?.score,
          revised: res.content?.revised,
          concluded: res.content?.concluded,
          studentAnswer: res.content?.answer ?? null,
        };

        if (!!res?.content?.feedback) {
          this.feedbacks = flattenDeep(
            res.content.feedback?.map((feedback) =>
              !!feedback.description
                ? [
                    {
                      id: undefined,
                      file: undefined,
                      title: feedback.created_at,
                      size: undefined,
                      extension: 'html',
                      type: TypeAttachment.HTML,
                      html: feedback?.description,
                      description: feedback?.description,
                      updated_at: feedback?.updated_at,
                      user: feedback?.user,
                    },
                    ...feedback.attachments,
                  ]
                : feedback.attachments.map((attachment) => ({ ...attachment, user: feedback?.user })),
            ),
          );
        }

        this.answer = {
          ...res,
          content: {
            ...res.content,
            attachment: res.content?.attachments.map((element) => ({
              id: element.id,
              extension: element.extension,
              file: element.file,
              hash: undefined,
              mymetype: undefined,
              size: element.size,
              title: element.title,
              type: element.type,
              url: element.url,
              vimeo_account: element.vimeo_account,
            })),
          },
        };

        this.info = {
          ...this.info,
          resubDate: this.answer?.date_end,
        };

        this.answer?.content?.attachment?.forEach((attachment) => {
          this.files.push(attachment);
        });

        this.setDeadLine();
        if (!!this.data?.resetForm) {
          this.form.reset({ activity_id: this.answer?.content?.activity_id });
          this.controls.studentAnswer = null;
          this.files = [];
        }
        this._cdRef.markForCheck();
      });
  }

  public onFilesChanged(files: UploadFile[]) {
    const isUploading = files.some((f) => f.status === 'uploading' || f.status === 'waiting');
    this.disable = isUploading;
    this._cdRef.markForCheck();
  }

  public setDeadLine() {
    if (!!this.controls.allow) {
      if (this.controls?.deadline === 0) {
        this.situationalPhrase = this._translateService.instant(`eclass.tarefa.encerra-hoje`);
      } else if (this.controls?.deadline >= 1 && this.controls.beforeDeadline) {
        if (this.controls?.deadline === 1) {
          this.situationalPhrase = this._translateService.instant(`eclass.tarefa.encerra-em-singular`, {
            deadLine: this.controls?.deadline,
          });
        } else {
          this.situationalPhrase = this._translateService.instant(`eclass.tarefa.encerra-em`, {
            deadLine: this.controls?.deadline,
          });
        }
      }
      if (!this.controls.beforeDeadline) {
        this.situationalPhrase = this._translateService.instant(`eclass.tarefa.em-atraso`);
      }
    } else {
      this.situationalPhrase = this._translateService.instant(`eclass.tarefa.finalizada`);
    }
  }

  public onClose() {
    if (this.form.dirty) {
      this._alertService
        .confirm({
          message: this._translateService.instant(`eclass.tarefa.sair-sem-salvar`),
        })
        .pipe(filter((result) => !!result))
        .subscribe(() => this.dialogRef.close());
      return;
    }

    this.dialogRef.close();
  }

  public onUploaded(event: UploadFile) {
    const file = {
      id: null,
      dropzoneId: event.id,
      file: event.uploadedFile,
      title: event.name,
      size: event.size,
      extension: event.name.split('.').pop()?.toLowerCase(),
      key: event.key,
      url: null,
    };
    this.files.push(file);
    this._cdRef.markForCheck();
  }

  public onSend() {
    this.form.get('attachments').setValue(this.files);
    if (this.files.length > 0 || !!this.form.get('answer').value) {
      this.send();
    } else {
      this._alertService.alert({
        title: 'status.atencao',
        message: this._translateService.instant(`eclass.tarefa.envio-regra`),
      });
    }
  }

  public icon(extension): string {
    return '/assets/images/files/' + extension + '.svg';
  }

  public onFileRemoved(targetId: string) {
    const targetFile = this.files.find((file) => String(file.id) === targetId || file.dropzoneId === targetId);

    if (targetFile?.id) {
      this._attachmentService.deleteAttachment(targetFile.id).subscribe((res) => {
        if (res.ret) {
          this._notificationService.success('geral.sucesso');
        } else {
          this._alertService.alert({ message: res.msg || 'geral.erro' });
        }
        this.files = this.files.filter((f) => String(f.id) !== targetId && f.dropzoneId !== targetId);
        this._cdRef.markForCheck();
      });
    } else {
      this.files = this.files.filter((f) => String(f.id) !== targetId && f.dropzoneId !== targetId);
      this._cdRef.markForCheck();
    }
  }

  public onOpenFeedbackAttachment(data: Attachment) {
    this._dialog.open(ModalFileViewerComponent, { data });
  }

  public getTranslate(translate: string): string {
    const key = 'eclass.' + translate;
    return this._translateService.instant(key);
  }

  public getDescription(description: string): string {
    return description.length > 30 ? description.substring(0, 30) + '...' : description;
  }

  public canDownload(extension: string) {
    const ext = ['pdf', 'jpg', 'jpeg', 'xls', 'xlsx', 'ppt', 'pptx', 'doc', 'docx', 'png'];

    return ext.includes(extension);
  }

  public onDownload(feedback: any) {
    const data = {
      title: feedback.title,
      extension: feedback.extension,
      url: feedback.file,
    };

    downloadFile(data);
  }

  private initForm() {
    this.form = this._formBuilder.group({
      id: undefined,
      activity_id: undefined,
      answer: undefined,
      app: 0,
      attachments: undefined,
      score: undefined,
    });
  }

  private send() {
    this._alertService
      .confirm({
        message: !!this.form.value.id
          ? this._translateService.instant('eclass.tarefa.alterar-atividade')
          : this._translateService.instant('eclass.tarefa.enviar-atividade'),
      })
      .pipe(
        filter((result) => !!result),
        switchMap(() =>
          !!this.form.value.id
            ? this._answerService.updateAnswer(this.form.get('id').value, this.form.value)
            : this._answerService.sendAnswer(this.form.value),
        ),
        switchMap(() =>
          this._alertService.success({ message: this._translateService.instant('geral.salvo-com-sucesso') }),
        ),
      )
      .subscribe(() => this.dialogRef.close(true));
  }

  private openVideo(url: string) {
    window.open(url, '_blank');
  }
}
